import { ImageOff } from 'lucide-react';

import type { ImageContent, ScriptElement } from '@/lib/schema.types';

interface Props {
  element: ScriptElement;
  /**
   * When `true`, the image fills its parent container (used by freeform mode
   * where the wrapper sets the explicit width/height). When `false`, the image
   * sizes itself according to `style.width` / `style.height` if present, or
   * falls back to its intrinsic size constrained by the column width.
   */
  fill?: boolean;
}

export function ImageElement({ element, fill }: Props) {
  const content = element.content as ImageContent | undefined;
  // rounded + shadow on by default, border off.
  const rounded = content?.rounded !== false;
  const showShadow = content?.shadow !== false;
  const bordered = !!content?.bordered;
  const radius = rounded ? ((element.style?.borderRadius as number | undefined) || 12) : 0;
  // Decoration that hugs the *visible* image: `drop-shadow` follows the rounded
  // silhouette (a `box-shadow` would trace the rectangular bounding box, and
  // with letterboxing that box is bigger than the picture), and the radius /
  // border sit on the image element itself.
  const deco: React.CSSProperties = {
    borderRadius: radius,
    filter: showShadow ? 'drop-shadow(0 8px 18px rgba(0,0,0,0.28))' : undefined,
    border: bordered ? '1px solid rgba(0,0,0,0.18)' : undefined,
  };
  const w = element.style?.width as number | string | undefined;
  const h = element.style?.height as number | string | undefined;
  const crop = content?.crop;

  if (!content?.src) {
    return (
      <div
        style={fill ? { width: '100%', height: '100%' } : undefined}
        className="flex aspect-video w-full items-center justify-center rounded-md bg-black/5 text-fg-subtle"
      >
        <ImageOff size={20} />
      </div>
    );
  }

  // When cropped, clip to the crop region: a wrapper hides the overflow and the
  // image is scaled (1/crop.w × 1/crop.h) + translated so the region fills it.
  // Here the wrapper IS the visible region, so the decoration goes on it.
  if (crop && crop.w > 0 && crop.h > 0) {
    const box: React.CSSProperties = fill
      ? { width: '100%', height: '100%' }
      : {
          width: typeof w === 'number' ? `${w}px` : w,
          height: typeof h === 'number' ? `${h}px` : h,
          maxWidth: '100%',
        };
    return (
      <div style={{ ...box, ...deco, overflow: 'hidden', position: 'relative' }}>
        <img
          src={content.src}
          alt={content.alt ?? ''}
          draggable={false}
          className="block max-w-none"
          style={{
            position: 'absolute',
            width: `${100 / crop.w}%`,
            height: `${100 / crop.h}%`,
            left: `${-(crop.x / crop.w) * 100}%`,
            top: `${-(crop.y / crop.h) * 100}%`,
            objectFit: 'cover',
          }}
        />
      </div>
    );
  }

  // The <img> element is sized to the picture (not the bounding box), so the
  // radius/border/shadow hug the image with no letterbox margin. In fill mode a
  // centering wrapper holds the leftover box space.
  const img = (
    <img
      src={content.src}
      alt={content.alt ?? ''}
      draggable={false}
      className="block"
      style={
        fill
          ? { maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', ...deco }
          : {
              width: typeof w === 'number' ? `${w}px` : w,
              height: typeof h === 'number' ? `${h}px` : h,
              maxWidth: '100%',
              objectFit: 'contain',
              ...deco,
            }
      }
    />
  );

  if (fill) {
    return (
      <div className="flex h-full w-full items-center justify-center">{img}</div>
    );
  }
  return img;
}
